import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Search, Eye, EyeOff, Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp,
  Lock, ExternalLink, Plug, Pencil, Save, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified admin panel listing all registered SEO providers.
 * Fetches providers dynamically from /api/settings/app/providers.
 *
 * Two card variants:
 * - DataForSEO: 2 fields (login + password), stored as "login:password"
 * - Other built-ins (Haloscan, SEMrush): single api_key field
 * - Custom slots: editable name/docs_url + api_key
 */
export default function GlobalSEOSettings() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [focusId, setFocusId] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/app/providers`, { withCredentials: true });
      setProviders(data.providers || []);
    } catch {
      // 401 non-admin — panel silently hidden
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ubs = params.get('ubersuggest');
    if (params.get('providers') === 'open') {
      setExpanded(true);
      setFocusId(params.get('focus') || null);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (ubs === 'connected') {
      toast.success('Ubersuggest connecté avec succès');
      setExpanded(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (ubs === 'error') {
      toast.error(`Échec connexion Ubersuggest : ${params.get('details') || 'erreur inconnue'}`);
      setExpanded(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!focusId || loading || providers.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="provider-card-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-[#EF6C00]', 'rounded-lg');
        setTimeout(() => el.classList.remove('ring-2', 'ring-[#EF6C00]', 'rounded-lg'), 4000);
      }
      setFocusId(null);
    }, 300);
    return () => clearTimeout(t);
  }, [focusId, loading, providers]);

  const configuredCount = providers.filter(p => p.configured).length;

  if (loading || providers.length === 0) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg" data-testid="global-seo-settings">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full p-4 flex items-center gap-3 hover:bg-neutral-50 transition-colors text-left"
        data-testid="toggle-providers-panel"
      >
        <div className="w-10 h-10 bg-[#002FA7]/10 rounded flex items-center justify-center flex-shrink-0">
          <Plug className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-semibold text-neutral-900">Providers de données SEO</h2>
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded-full font-medium">
              {configuredCount}/{providers.length} configurés
            </span>
          </div>
          <p className="text-xs text-neutral-500 mt-0.5">
            Chaque agence renseigne ses propres clés. Les emplacements libres permettent d'ajouter n'importe quel provider.
          </p>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-neutral-400" /> : <ChevronDown className="w-5 h-5 text-neutral-400" />}
      </button>

      {expanded && (
        <div className="border-t border-neutral-200 divide-y divide-neutral-100">
          {providers.map(p => (
            <ProviderCard key={p.id} provider={p} onChange={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider, onChange }) {
  const isDataForSEO = provider.id === 'dataforseo';
  const isOAuth = provider.key_format === 'oauth';
  const isCustom = !provider.builtin;

  // DataForSEO: split login/password
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  // Single-key providers
  const [key, setKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  // Custom slot metadata edition
  const [editingMeta, setEditingMeta] = useState(false);
  const [slotName, setSlotName] = useState(provider.name || '');
  const [slotDocsUrl, setSlotDocsUrl] = useState(provider.docs_url || '');

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setSlotName(provider.name || '');
    setSlotDocsUrl(provider.docs_url || '');
  }, [provider.name, provider.docs_url]);

  const displayName = provider.name && provider.name.trim()
    ? provider.name
    : (isCustom ? `Provider personnalisé ${provider.id.replace('custom_', '#')}` : provider.id);

  const buildPayload = () => {
    if (isDataForSEO) {
      if (!login.trim() || !password.trim()) return null;
      return { api_key: `${login.trim()}:${password.trim()}` };
    }
    if (!key.trim()) return null;
    return { api_key: key.trim() };
  };

  const save = async () => {
    const payload = buildPayload();
    if (!payload) {
      toast.error(isDataForSEO ? 'Veuillez saisir login et password' : 'Veuillez saisir une clé');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/app/providers/${provider.id}/key`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Clé ${displayName} enregistrée`);
      setLogin(''); setPassword(''); setKey('');
      setTestResult(null);
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // If user typed a new key/credentials, test those. Else test the stored one.
      const payload = buildPayload() || {};
      const { data } = await axios.post(
        `${API}/settings/app/providers/${provider.id}/test`,
        payload,
        { withCredentials: true }
      );
      setTestResult(data);
      if (data.success) toast.success(`${displayName} — connexion OK`);
      else toast.error(data.error || 'Test échoué');
    } catch (e) {
      const err = e.response?.data?.detail || e.message;
      setTestResult({ success: false, error: err });
      toast.error(err);
    } finally {
      setTesting(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Supprimer la clé ${displayName} ?`)) return;
    try {
      await axios.put(
        `${API}/settings/app/providers/${provider.id}/key`,
        { api_key: '' },
        { withCredentials: true }
      );
      toast.success('Clé supprimée');
      setLogin(''); setPassword(''); setKey('');
      setTestResult(null);
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const saveMeta = async () => {
    if (!slotName.trim()) {
      toast.error('Veuillez saisir un nom pour ce provider');
      return;
    }
    try {
      // Metadata-only update: do NOT send api_key so the backend preserves the existing key.
      await axios.put(
        `${API}/settings/app/providers/${provider.id}/key`,
        {
          name: slotName.trim(),
          docs_url: slotDocsUrl.trim(),
        },
        { withCredentials: true }
      );
      toast.success('Emplacement mis à jour');
      setEditingMeta(false);
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const connectOAuth = async () => {
    setSaving(true);
    try {
      const { data } = await axios.get(
        `${API}/settings/app/providers/${provider.id}/connect`,
        { withCredentials: true }
      );
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du démarrage de la connexion');
      setSaving(false);
    }
  };

  const disconnectOAuth = async () => {
    if (!window.confirm(`Déconnecter ${displayName} ?`)) return;
    try {
      await axios.delete(
        `${API}/settings/app/providers/${provider.id}/disconnect`,
        { withCredentials: true }
      );
      toast.success(`${displayName} déconnecté`);
      setTestResult(null);
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-4 space-y-3" data-testid={`provider-card-${provider.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 bg-neutral-100 rounded flex items-center justify-center flex-shrink-0">
            {provider.configured ? <Lock className="w-4 h-4 text-green-600" /> : <Search className="w-4 h-4 text-neutral-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-neutral-900" data-testid={`provider-name-${provider.id}`}>
                {displayName}
              </h3>
              {provider.configured ? (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded-full font-medium uppercase">
                  <CheckCircle className="w-3 h-3" /> Configuré
                </span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium uppercase">
                  Non configuré
                </span>
              )}
              {isCustom && (
                <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium uppercase">
                  Emplacement libre
                </span>
              )}
              {isCustom && !editingMeta && (
                <button
                  type="button"
                  onClick={() => setEditingMeta(true)}
                  className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline"
                  data-testid={`provider-edit-meta-${provider.id}`}
                >
                  <Pencil className="w-3 h-3" /> Renommer
                </button>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">{provider.short_desc}</p>
            {provider.docs_url && !editingMeta && (
              <a
                href={provider.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline mt-1"
              >
                Documentation / clé API <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Custom slot metadata editor */}
      {isCustom && editingMeta && (
        <div className="bg-neutral-50 border border-neutral-200 rounded p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-neutral-600 uppercase">Nom affiché</label>
              <Input
                value={slotName}
                onChange={e => setSlotName(e.target.value)}
                placeholder="Ex. Ahrefs, Serpstat, etc."
                data-testid={`provider-name-input-${provider.id}`}
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-neutral-600 uppercase">URL de documentation (optionnel)</label>
              <Input
                value={slotDocsUrl}
                onChange={e => setSlotDocsUrl(e.target.value)}
                placeholder="https://..."
                data-testid={`provider-docs-input-${provider.id}`}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditingMeta(false);
                setSlotName(provider.name || '');
                setSlotDocsUrl(provider.docs_url || '');
              }}
            >
              <X className="w-4 h-4" /> Annuler
            </Button>
            <Button
              size="sm"
              onClick={saveMeta}
              className="bg-[#002FA7] hover:bg-[#002277]"
              data-testid={`provider-save-meta-${provider.id}`}
            >
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Credentials inputs */}
      {isOAuth ? (
        <div className="space-y-2">
          {provider.configured ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-neutral-600" data-testid={`provider-oauth-account-${provider.id}`}>
                Compte connecté : <b>{provider.connected_email || 'compte Ubersuggest'}</b>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={test}
                  disabled={testing}
                  data-testid={`provider-test-${provider.id}`}
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tester'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={disconnectOAuth}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid={`provider-disconnect-${provider.id}`}
                >
                  Déconnecter
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-neutral-500 flex-1 min-w-[200px]">
                Aucune clé à copier : cliquez et autorisez l'accès depuis votre compte Ubersuggest (abonnement payant requis).
              </p>
              <Button
                size="sm"
                onClick={connectOAuth}
                disabled={saving}
                className="bg-[#EF6C00] hover:bg-[#D65F00] text-white"
                data-testid={`provider-connect-${provider.id}`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plug className="w-4 h-4 mr-1" />}
                Connecter mon compte Ubersuggest
              </Button>
            </div>
          )}
        </div>
      ) : isDataForSEO ? (
        <div className="space-y-2">
          {/* Explicit help block for DataForSEO */}
          <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
            <p className="font-medium text-blue-900">Où trouver ces identifiants ?</p>
            <ul className="list-disc ml-4 text-blue-800 space-y-0.5">
              <li><b>Login</b> = votre <b>email DataForSEO</b> (celui du compte <code className="bg-white px-1 rounded">app.dataforseo.com</code>)</li>
              <li><b>Clé API</b> = l'<b>API Password</b> envoyé par DataForSEO par email (chaîne hexadécimale type <code className="bg-white px-1 rounded">1a2b3c4d5e6f7890</code>)</li>
            </ul>
            <p className="text-blue-800">
              Vous ne l'avez plus ? <a href="https://app.dataforseo.com/api-access" target="_blank" rel="noopener noreferrer" className="underline font-medium">Regénérez-la ici</a>.
            </p>
            {provider.stored_login_masked && (
              <p className="text-blue-900 mt-1">
                Login actuellement enregistré : <code className="bg-white px-1 rounded">{provider.stored_login_masked}</code>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-neutral-600 uppercase">Login DataForSEO (email)</label>
              <Input
                value={login}
                onChange={e => setLogin(e.target.value)}
                placeholder={provider.configured ? 'email (•••• — conservé)' : 'votre-email@exemple.com'}
                data-testid="provider-dataforseo-login"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-neutral-600 uppercase">Clé API (API Password)</label>
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={provider.configured ? '•••• (conservée)' : 'ex. 1a2b3c4d5e6f7890'}
                  className="pr-10"
                  data-testid="provider-dataforseo-password"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                  aria-label="Afficher le mot de passe"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <ActionButtons
            provider={provider}
            canSave={!!(login.trim() && password.trim())}
            canTest={!!(login.trim() && password.trim()) || provider.configured}
            saving={saving} testing={testing}
            onSave={save} onTest={test} onClear={clear}
            displayName={displayName}
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              type={showKey ? 'text' : 'password'}
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder={provider.configured ? '•••••••• (laissez vide pour conserver l\'actuelle)' : `Collez votre clé ${displayName}`}
              className="pr-10"
              data-testid={`provider-key-input-${provider.id}`}
            />
            <button
              type="button"
              onClick={() => setShowKey(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !key.trim()}
            className="bg-[#002FA7] hover:bg-[#002277]"
            data-testid={`provider-save-${provider.id}`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={test}
            disabled={testing || (!key.trim() && !provider.configured)}
            data-testid={`provider-test-${provider.id}`}
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tester'}
          </Button>
          {provider.configured && (
            <Button
              size="sm"
              variant="outline"
              onClick={clear}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid={`provider-clear-${provider.id}`}
            >
              Effacer
            </Button>
          )}
        </div>
      )}

      {testResult && (
        <div className={`flex items-start gap-2 text-xs p-2 rounded ${
          testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`} data-testid={`provider-test-result-${provider.id}`}>
          {testResult.success
            ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            : <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div>{testResult.success ? (testResult.note || 'Connexion OK') : (testResult.error || 'Test échoué')}</div>
            {!testResult.success && isDataForSEO && testResult.error && testResult.error.includes('401') && (
              <div className="mt-1 text-[11px] text-red-700">
                💡 Vérifiez bien que le <b>Login</b> est votre <b>email DataForSEO</b> (pas un autre identifiant) et que la <b>Clé API</b> correspond à la valeur reçue par email (pas le mot de passe du compte).
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionButtons({ provider, canSave, canTest, saving, testing, onSave, onTest, onClear }) {
  return (
    <div className="flex gap-2 justify-end">
      <Button
        size="sm"
        onClick={onSave}
        disabled={saving || !canSave}
        className="bg-[#002FA7] hover:bg-[#002277]"
        data-testid={`provider-save-${provider.id}`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onTest}
        disabled={testing || !canTest}
        data-testid={`provider-test-${provider.id}`}
      >
        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tester'}
      </Button>
      {provider.configured && (
        <Button
          size="sm"
          variant="outline"
          onClick={onClear}
          className="text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid={`provider-clear-${provider.id}`}
        >
          Effacer
        </Button>
      )}
    </div>
  );
}
